import {Button, Group, Modal, NumberInput, Select, Stack, Text, TextInput} from '@mantine/core'
import {useEffect, useState} from 'react'

import type {RentalAssetType} from '../assets/rental-assets/schemas'
import {useRentalAssetTypes} from '../assets/rental-assets/use-rental-asset-types'
import type {WorkerType} from '../assets/worker-types/schemas'
import {useWorkerTypes} from '../assets/worker-types/use-worker-types'
import {useInvalidateDispatchAttentionCount} from '../dispatch/use-dispatch'
import {useInvalidateProjectDeployedEquipment} from '../project-deployed-equipment/use-project-deployed-equipment'
import {requirementsToInputs} from './assignment-grouping-utils'
import {truckAndDriverBillingWarning} from './billing-checks'
import {
    billingTypeForRate,
    CREW_BILLING_TYPE_OPTIONS,
    EQUIPMENT_BILLING_TYPE_OPTIONS,
    type RequirementBillingType,
} from './billing-types'
import type {JobDataLayer} from './job-data-layer'
import {
    billsEachLine,
    lineTakesRate,
    orderRatePhrase,
    RENTAL_BILLING_TYPE_OPTIONS,
    requirementsAsBilled,
} from './order-billing'
import {getAvailableSlots} from './requirement-utils'
import {TruckDriverBillingAlert} from './truck-driver-billing-warning'
import type {JobDetail, JobRequirementValue} from './types'

type AddRequirementModalProps = {
    opened: boolean
    job: JobDetail
    jobDataLayer: JobDataLayer
    rentalAssetTypes?: RentalAssetType[]
    workerTypes?: WorkerType[]
    onClose: () => void
    onJobUpdated: (job: JobDetail) => void
    onRequirementAdded?: (groupKey: string) => void
}

function requirementTypeOptions(
    requirements: JobDetail['requirements'],
    rentalAssetTypes: RentalAssetType[],
    workerTypes: WorkerType[],
): Array<{label: string; value: string}> {
    const slots = getAvailableSlots(
        requirements.map((requirement) => ({
            requirementType: requirement.requirementType,
            quantity: requirement.quantity,
            workerTypeId: requirement.workerTypeId ?? null,
            rentalAssetTypeId: requirement.rentalAssetTypeId,
            billingRate: requirement.billingRate,
            billingType: requirement.billingType,
        })),
        rentalAssetTypes,
        workerTypes,
    )
    const options: Array<{label: string; value: string}> = []

    for (const typeId of slots.availableWorkerTypeIds) {
        const typeName = workerTypes.find((type) => type.id === typeId)?.name ?? 'Worker'
        options.push({label: typeName, value: `worker:${typeId}`})
    }

    for (const typeId of slots.availableRentalAssetTypeIds) {
        const typeName = rentalAssetTypes.find((type) => type.id === typeId)?.name ?? 'Rental asset'
        options.push({label: typeName, value: typeId})
    }

    return options
}

function draftFromSelection(selectedValue: string): JobRequirementValue {
    if (selectedValue.startsWith('worker:')) {
        return {
            requirementType: 'worker',
            quantity: 1,
            workerTypeId: selectedValue.slice('worker:'.length),
            rentalAssetTypeId: null,
            billingRate: null,
            billingType: null,
        }
    }

    return {
        requirementType: 'rental_asset',
        quantity: 1,
        workerTypeId: null,
        rentalAssetTypeId: selectedValue,
        billingRate: null,
        billingType: null,
    }
}

export function AddRequirementModal({
    opened,
    job,
    jobDataLayer,
    rentalAssetTypes: rentalAssetTypesProp,
    workerTypes: workerTypesProp,
    onClose,
    onJobUpdated,
    onRequirementAdded,
}: AddRequirementModalProps) {
    const {data: fetchedRentalAssetTypes = []} = useRentalAssetTypes({
        enabled: opened && rentalAssetTypesProp == null,
    })
    const {data: fetchedWorkerTypes = []} = useWorkerTypes({
        enabled: opened && workerTypesProp == null,
    })
    const rentalAssetTypes = rentalAssetTypesProp ?? fetchedRentalAssetTypes
    const workerTypes = workerTypesProp ?? fetchedWorkerTypes
    const [selectedType, setSelectedType] = useState<string | null>(null)
    const [quantity, setQuantity] = useState(1)
    const [billingRate, setBillingRate] = useState('')
    const [billingType, setBillingType] = useState<RequirementBillingType | null>(null)
    const [billingWarning, setBillingWarning] = useState<string | null>(null)
    const [saving, setSaving] = useState(false)
    const [errorMessage, setErrorMessage] = useState<string | null>(null)
    const invalidateDispatchAttentionCount = useInvalidateDispatchAttentionCount()
    const invalidateProjectDeployedEquipment = useInvalidateProjectDeployedEquipment()

    const options = requirementTypeOptions(job.requirements, rentalAssetTypes, workerTypes)
    const eachLine = billsEachLine(job.billingMethod)
    const isRentalType = (typeId: string | null | undefined) =>
        typeId != null &&
        rentalAssetTypes.find((type) => type.id === typeId)?.isDrivenTruck !== true
    const optionValuesKey = options.map((option) => option.value).join(',')

    useEffect(() => {
        if (!opened) {
            return
        }

        setSelectedType(options[0]?.value ?? null)
        setQuantity(1)
        setBillingRate('')
        setBillingType(null)
        setBillingWarning(null)
        setErrorMessage(null)
    }, [opened])

    useEffect(() => {
        if (!opened) {
            return
        }

        setSelectedType((current) => {
            if (current != null && options.some((option) => option.value === current)) {
                return current
            }

            return options[0]?.value ?? null
        })
    }, [optionValuesKey])

    // Reset billing fields when selected type changes
    useEffect(() => {
        setBillingRate('')
        setBillingType(null)
        setBillingWarning(null)
        setErrorMessage(null)
    }, [selectedType])

    const selectedIsTruck =
        selectedType != null &&
        rentalAssetTypes.some((type) => type.id === selectedType && type.isDrivenTruck)
    const selectedTakesRate = lineTakesRate(job.billingMethod, {
        isRentalEquipment:
            selectedType != null && !selectedType.startsWith('worker:') && !selectedIsTruck,
    })

    function handleBillingRateChange(value: string) {
        setBillingRate(value)
        setBillingWarning(null)
        if (value.trim() === '') {
            return
        }

        setBillingType((current) =>
            billingTypeForRate(
                value,
                current,
                selectedType?.startsWith('worker:')
                    ? 'worker'
                    : selectedIsTruck
                      ? 'truck'
                      : 'equipment',
            ),
        )
    }

    async function handleSave({confirmed = false}: {confirmed?: boolean} = {}) {
        if (!selectedType) {
            return
        }

        const draft = draftFromSelection(selectedType)
        draft.quantity = quantity

        const hasBillingRate = billingRate.trim() !== ''
        if (billingType !== null && !hasBillingRate) {
            setErrorMessage('Billing rate is required')
            return
        }
        draft.billingRate = selectedTakesRate && hasBillingRate ? billingRate : null
        draft.billingType = selectedTakesRate && hasBillingRate ? billingType : null

        const added = {
            requirementType: draft.requirementType,
            quantity: draft.quantity,
            workerTypeId: draft.workerTypeId ?? undefined,
            rentalAssetTypeId: draft.rentalAssetTypeId,
            billingRate: draft.billingRate,
            billingType: draft.billingType,
        }
        const nextRequirements = requirementsAsBilled(
            job.billingMethod,
            [...requirementsToInputs(job.requirements), added],
            (requirement) =>
                requirement.requirementType === 'rental_asset' &&
                isRentalType(requirement.rentalAssetTypeId),
        )
        const warning = truckAndDriverBillingWarning(
            nextRequirements,
            {workerTypes, rentalAssetTypes},
            {added},
        )
        if (warning != null && !confirmed) {
            setBillingWarning(warning)
            return
        }

        setBillingWarning(null)
        setSaving(true)
        setErrorMessage(null)
        try {
            const updatedJob = await jobDataLayer.saveRequirements(job.id, nextRequirements)
            onJobUpdated(updatedJob)
            invalidateDispatchAttentionCount()
            invalidateProjectDeployedEquipment(job.projectId)
            onRequirementAdded?.(
                draft.requirementType === 'worker'
                    ? (draft.workerTypeId ?? 'worker')
                    : (draft.rentalAssetTypeId ?? selectedType),
            )
            onClose()
        } catch (error) {
            setErrorMessage(error instanceof Error ? error.message : 'Failed to add requirement')
        } finally {
            setSaving(false)
        }
    }

    return (
        <Modal opened={opened} onClose={onClose} title="Add requirement">
            <Stack gap="md">
                <Select
                    label="Entity type"
                    data={options}
                    value={selectedType}
                    onChange={setSelectedType}
                    disabled={options.length === 0}
                />
                <NumberInput
                    label="Quantity"
                    min={1}
                    value={quantity}
                    onChange={(value) => setQuantity(typeof value === 'number' ? value : 1)}
                />
                {selectedType && !selectedTakesRate && job.billingMethod ? (
                    <Text size="sm" c="dimmed" data-testid="order-rate-lines-note">
                        Billed {orderRatePhrase(job.billingMethod)}. Crew and truck lines take a
                        count only.
                    </Text>
                ) : null}
                {selectedType && selectedTakesRate && (
                    <>
                        <Select
                            label="Billing type"
                            data={[
                                {
                                    value: 'none',
                                    label: selectedType.startsWith('worker:')
                                        ? "Don't bill this worker"
                                        : "Don't bill this equipment",
                                },
                                ...(!eachLine
                                    ? RENTAL_BILLING_TYPE_OPTIONS
                                    : selectedType.startsWith('worker:')
                                      ? CREW_BILLING_TYPE_OPTIONS
                                      : EQUIPMENT_BILLING_TYPE_OPTIONS),
                            ]}
                            value={billingType ?? 'none'}
                            onChange={(value) => {
                                const newBillingType =
                                    value === 'none' ? null : (value as RequirementBillingType)
                                setBillingType(newBillingType)
                                setBillingWarning(null)
                                if (newBillingType === null) {
                                    setBillingRate('')
                                }
                            }}
                            clearable={false}
                        />
                        <TextInput
                            label="Billing rate"
                            placeholder="0.00"
                            value={billingRate}
                            onChange={(event) => handleBillingRateChange(event.currentTarget.value)}
                        />
                        {billingWarning && (
                            <TruckDriverBillingAlert
                                message={billingWarning}
                                loading={saving}
                                onContinue={() => void handleSave({confirmed: true})}
                                onGoBack={() => setBillingWarning(null)}
                            />
                        )}
                    </>
                )}
                {errorMessage && (
                    <Button variant="subtle" color="red" disabled>
                        {errorMessage}
                    </Button>
                )}
                <Group justify="flex-end">
                    <Button variant="default" onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => void handleSave()}
                        loading={saving}
                        disabled={!selectedType}
                    >
                        Add requirement
                    </Button>
                </Group>
            </Stack>
        </Modal>
    )
}
